import { useCallback, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import * as yup from "yup";

import api from "services/api";

import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import CONSTANTS from "constants/constant";
import REGEX from "constants/regex";
import ERRORS from "constants/errors";

import ActionEnum from "enum/action";

import { RootStateDto } from "dto/redux/store-dto";
import ErrorResponseDto from "dto/services/error-response-dto";
import AppRecipientEmailArrayInputDto from "dto/components/pages/recipient-email/app-recipient-email-array-input-dto";

import AppInput from "components/app-input";
import AppResourceArrayWrapper from "../page-resources/app-resource-array-wrapper";

const AppRecipientEmailArrayInput = ({ detail }: AppRecipientEmailArrayInputDto) => {
	const { customerId } = useSelector((state: RootStateDto) => state.profile);
	const [focusIndex, setFocusIndex] = useState<null | number>(null);

	const initialValues = useMemo(() => {
		if (Array.isArray(detail.pojo) && detail.pojo.length > 0) return detail.pojo as string[];

		if (typeof detail.pojo === "string" && detail.pojo.trim() !== "") return [detail.pojo];

		return [""];
	}, [detail.pojo]);

	//prettier-ignore
	const validationSchema = yup.object({
		data: yup.array().of(
            yup.string().matches(REGEX.EMAIL, ERRORS.EMAIL).test("required", ERRORS.REQUIRED, function (value, context) {
                if (context.path === "data[0]") return !!value && value.trim() !== "";
                return true;
            })
        )
        .test("min-one-email", ERRORS.MIN_ONE_EMAIL, function (value) {
            return (value && value.length > 0 && value.some((v) => v && v.trim() !== "")) ;
        }),
	});

	const formik = useFormik({
		initialValues: { data: initialValues },
		validationSchema: validationSchema,
		onSubmit: async (values) => {
			const filterEmptyString = values.data.filter((item: any) => item);

			const payload = { customerId: customerId, template: detail.template, page: detail.page, key: detail.key, pojo: filterEmptyString };

			try {
				await api.post.emails.update(payload);

				formik.setFieldValue("data", filterEmptyString);

				serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);
			} catch (error) {
				const err = error as Error | ErrorResponseDto;

				serveRequestErrors(err);
			}
		},
	});

	const arrayLevelError = formik.submitCount > 0 && typeof formik.errors.data === "string" ? formik.errors.data : null;

	const onHandleAdd = useCallback(() => {
		const newData = "";
		const isLastValueEmpty = formik.values.data.length > 0 ? formik.values.data.slice(-1)[0] : true;

		if (formik.values.data.length < 5 && isLastValueEmpty) {
			formik.setFieldValue("data", [...formik.values.data, newData]);

			setFocusIndex(formik.values.data.length);
		}
	}, [formik]);

	//prettier-ignore
	const onHandleInputUpdate = useCallback((index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedValue = event.target.value;

        const updatedData = [...formik.values.data];

        updatedData[index] = updatedValue;

        formik.setFieldValue("data", updatedData);
    }, [formik]);

	//prettier-ignore
	const onHandleDelete = useCallback((index: number) => {
        const dataAfterRemoved = formik.values.data.filter((_, i) => i !== index);

        formik.setFieldValue("data", dataAfterRemoved);
    }, [formik]);

	const getTitle = (page: string) => {
		switch (page) {
			case "page.enquiry":
				return "Enquiry Recipient Email";
			case "page.career":
				return "Job Application Email";
			default:
				return "Recipient Email";
		}
	};

	return (
		<AppResourceArrayWrapper onAdd={onHandleAdd} onUpdate={formik.handleSubmit} title={getTitle(detail.page)} error={arrayLevelError} disabled={formik.values.data.length >= 5}>
			<div className="array-wrapper__inputs">
				{formik.values.data.map((item, index) => (
					<div key={`ai-${index}`} className="array-wrapper__input-wrapper">
						<AppInput
							type="text"
							name={`data[${index}]`}
							value={item}
							placeholder={"Enter email"}
							onChange={(e) => onHandleInputUpdate(index, e)}
							defaultActiveIndex={focusIndex === index}
							error={formik.errors.data?.[index] || (index === 0 && formik.errors.data && typeof formik.errors.data === "string" ? formik.errors.data : "")}
							touched={typeof formik.touched.data === "object" && formik.touched.data?.[index] ? true : false}
							onBlur={formik.handleBlur}
							onDelete={() => onHandleDelete(index)}
						/>
					</div>
				))}
			</div>
		</AppResourceArrayWrapper>
	);
};

export default AppRecipientEmailArrayInput;
